import {
    buildOptionalParams,
    buildPath,
    buildTemplatePath,
    CrudMode,
    Dialog,
    handleClose,
    PageRoute,
    ScreenType,
    useParams,
    useUnsavedChanges,
    useValidationErrors,
} from "@compass/core";
import {
    Lep,
    LEPEditResource,
    LEPViewResource,
    Permission
} from "generated/openApi";
import { createContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import { LEPPageHeader } from "../LepPage/LEPPageHeader";
import { LEPPageBody } from "../LepPage/LEPPageBody";

const routeName = "lep";

class Params {
    id: number = 0;
    crudMode: CrudMode = "view";
    screenType: ScreenType = "standard";
}

class OptionalParams {
    closeOnSave?: boolean = false;
}

export const lepPageRoute: PageRoute<Params, OptionalParams> = {
    handle: "LEP",
    templatePath: buildTemplatePath(routeName, Params),
    getPath: (params) => buildPath(routeName, params),
    getOptionalParams: (params) => buildOptionalParams(params),
};

export type LEPContextValue = {
    onCreate?: (id: number) => void;
};

export const LEPContext = createContext<LEPContextValue>({} as LEPContextValue);

export const LEPPage = () => {
    const ROOT_PERMISSION: Permission = Permission.SystemUtility;
    const navigate = useNavigate();
    const { params, optionalParams } = useParams(routeName, Params, OptionalParams);
    const [lep, setLep] = useState<Lep>({} as Lep);
    const [initLep, setInitLep] = useState<Lep>(lep);
    const [viewResource, setViewResource] = useState<LEPViewResource>({});
    const [editResource, setEditResource] = useState<LEPEditResource>({});
    const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [lep] });
    const { validationErrors, setValidationErrors } = useValidationErrors();
    const [closeOnSave] = useState<boolean>(optionalParams.closeOnSave || false);

    useEffect(() => {
        fetch(params.id);

        ApiService.lep.getViewResource().then((response) => {
            setViewResource(response);
        });
    }, []);

    useEffect(() => {
        if (params.crudMode === "edit") {
            ApiService.lep.getEditResource().then((response) => {
                setEditResource(response);
            });
        }
    }, [params.crudMode]);

    const reroute = (id: number, crudMode: CrudMode) =>
        navigate(`../${lepPageRoute.getPath({ id: id, crudMode: crudMode, screenType: params.screenType, })}`);

    const fetch = (id: number) => {
        if (id > 0) {
            ApiService.lep.get(id).then((response) => {
                setLep(response);
                setInitLep(response);
                resetUnsavedChanges([response]);
            });
        } else {
            // For new
            setLep({} as Lep);
            setInitLep({} as Lep);
            resetUnsavedChanges([{} as Lep]);
        }
    };

    const pageHeader = (
        <LEPPageHeader
            rootPermission={ROOT_PERMISSION}
            handle={lepPageRoute.handle}
            crudMode={params.crudMode}
            reroute={reroute}
            fetch={fetch}
            lep={lep}
            setLep={setLep}
            initLep={initLep}
            setValidationErrors={setValidationErrors}
            unsavedChanges={unsavedChanges}
            resetUnsavedChanges={resetUnsavedChanges}
            closeOnSave={closeOnSave}
        />
    );

    const pageBody = (
        <LEPPageBody
            lep={lep}
            setLep={setLep}
            viewResource={viewResource}
            editResource={editResource}
            crudMode={params.crudMode}
            validationErrors={validationErrors}
        />
    );

    return (
        <Dialog
            isForm
            open={true}
            onClose={() => handleClose(navigate, unsavedChanges)}
            maxWidth="md"
            header={pageHeader}
            content={pageBody}
        />
    );
};